import {
  FIVE_MINUTES,
  ORGANIZATION_CONTEXT_CHANNEL,
  ORGANIZATION_CONTEXT_HEADER,
  queryKeys
} from '@/api'
import { idmQueryKeys } from '@/api/momo/idm/idm-query-keys'
import { updateCurrentOrganization } from '@/api/momo/idm/users'
import { getAllOrganizations } from '@/api/momo/users/organizations'
import { useAuthContext } from '@/hooks/use-auth-context'
import { Select, Tooltip } from '@mantine/core'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { usePathname, useRouter } from 'next/navigation'
import { useEffect, useMemo, useRef } from 'react'
import { toastError } from '../../toast/toast'
import { Icon } from '@/components/icon'
import { faEye as faEyeRegular } from '@fortawesome/free-regular-svg-icons'
import { OrganizationTypes } from '@/api/generated/model'
import { SuperAdminRoleID } from '@/constants/role-ids'

// List-page roots any organization can view; detail pages collapse to their
// root because their IDs belong to the previous organization.
const orgSafeRoots = [
  '/agreements',
  '/pricesheets',
  '/branches',
  '/claim-management/claims',
  '/users',
  '/partners'
]

// Where to land after switching organizations: stay in place when the page is
// org-independent, drop to the section's list page when the new org can view
// it, otherwise go home.
function destinationAfterOrgSwitch(
  pathname: string,
  newOrgType?: OrganizationTypes
): string {
  if (pathname.startsWith('/admin') || pathname.startsWith('/profile')) {
    return pathname
  }

  const safeRoot = orgSafeRoots.find((root) => pathname.startsWith(root))
  if (safeRoot) {
    return safeRoot
  }

  if (
    pathname.startsWith('/claim-management/transactions') &&
    newOrgType === OrganizationTypes.Distributor
  ) {
    return '/claim-management/transactions'
  }

  if (
    pathname.startsWith('/products') &&
    newOrgType === OrganizationTypes.Manufacturer
  ) {
    return '/products'
  }

  return '/'
}

// Lets a Super Admin view the application as another organization. The
// selection is persisted server-side and every org-scoped endpoint follows it,
// so the app behaves as if the user were an admin of the selected org.
// Selecting the home organization switches back.
export function OrgDropdown() {
  const queryClient = useQueryClient()
  const router = useRouter()
  const pathname = usePathname()
  const { account } = useAuthContext()

  const isSuperAdmin = account?.Role.ID === SuperAdminRoleID

  const { data: organizations } = useQuery({
    queryKey: queryKeys.organizations.organizations(),
    queryFn: getAllOrganizations,
    enabled: isSuperAdmin,
    staleTime: FIVE_MINUTES
  })

  // The organization selection is stored per account, not per tab, so a switch
  // here changes what every other open tab is acting as. Broadcast the switch
  // so those tabs converge immediately instead of keeping the old organization
  // on screen (their writes are rejected by the API either way). Messages
  // posted on a channel are not delivered back to that same channel instance,
  // so posting and listening on one instance avoids reacting to our own switch.
  const channelRef = useRef<BroadcastChannel | null>(null)

  useEffect(() => {
    if (!account || !isSuperAdmin || typeof BroadcastChannel === 'undefined') {
      return
    }

    const channel = new BroadcastChannel(ORGANIZATION_CONTEXT_CHANNEL)
    channelRef.current = channel

    channel.onmessage = (event: MessageEvent<string>) => {
      const newOrganizationID = event.data
      if (newOrganizationID === account.OrganizationID) {
        return
      }

      // Stamp the new context before refetching: some reads are POST requests
      // (grid searches) and would otherwise be rejected as stale writes.
      axios.defaults.headers.common[ORGANIZATION_CONTEXT_HEADER] =
        newOrganizationID

      const newOrgType = organizations?.find(
        (organization) => organization.ID === newOrganizationID
      )?.OrganizationType
      router.push(destinationAfterOrgSwitch(pathname, newOrgType))
      queryClient.invalidateQueries()
    }

    return () => {
      channel.close()
      channelRef.current = null
    }
  }, [account, isSuperAdmin, organizations, pathname, queryClient, router])

  const { mutate: setCurrentOrganizationID, isPending } = useMutation({
    mutationFn: async (organizationID: string) => {
      return updateCurrentOrganization(organizationID)
    },
    onSuccess: (updatedUser) => {
      // Stamp the new context before refetching: the auth-context effect only
      // updates the header after a render, and the refetches below include
      // POST-based grid reads that would be rejected with the old header.
      axios.defaults.headers.common[ORGANIZATION_CONTEXT_HEADER] =
        updatedUser.OrganizationID

      // Seed the current-user query from the response so the switch is visible
      // immediately, and invalidate (not clear) so mounted queries actually
      // refetch — clear() leaves them holding removed data.
      queryClient.setQueryData(idmQueryKeys.users.current(), updatedUser)

      const newOrgType = organizations?.find(
        (organization) => organization.ID === updatedUser.OrganizationID
      )?.OrganizationType
      router.push(destinationAfterOrgSwitch(pathname, newOrgType))
      queryClient.invalidateQueries()
      channelRef.current?.postMessage(updatedUser.OrganizationID)
    },
    onError: () => {
      toastError(
        'An unexpected error occurred while updating your current organization.'
      )
    }
  })

  const options = useMemo(() => {
    return (
      organizations
        ?.map((organization) => ({
          value: organization.ID,
          label: organization.Name
        }))
        .sort((a, b) => a.label.localeCompare(b.label)) ?? []
    )
  }, [organizations])

  if (!account || !isSuperAdmin) {
    return null
  }

  // Highlight that the site is showing another organization's data.
  const isViewingAsOtherOrg =
    account.OrganizationID !== account.HomeOrganizationID

  return (
    <Select
      aria-label="Current organization"
      data={options}
      value={account.OrganizationID}
      onChange={(organizationID) => {
        if (organizationID && organizationID !== account.OrganizationID) {
          setCurrentOrganizationID(organizationID)
        }
      }}
      searchable
      disabled={isPending}
      allowDeselect={false}
      w={230}
      leftSectionPointerEvents="all"
      leftSection={
        isViewingAsOtherOrg ? (
          <Tooltip label="You are viewing Celerity as an admin of this organization">
            <span className="flex items-center">
              <Icon icon={faEyeRegular} size={12} />
            </span>
          </Tooltip>
        ) : undefined
      }
      comboboxProps={{ shadow: 'md' }}
      styles={{
        // Unmissably red while viewing as another organization.
        input: isViewingAsOtherOrg
          ? {
              backgroundColor: 'var(--mantine-color-red-7)',
              color: 'white',
              fontWeight: 700,
              borderColor: 'var(--mantine-color-red-9)'
            }
          : {
              backgroundColor: 'transparent',
              color: 'white',
              fontWeight: 600,
              borderColor: 'rgba(255, 255, 255, 0.4)'
            },
        section: {
          color: 'white'
        }
      }}
    />
  )
}
